import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Upload, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BulkUpload = () => {
  const navigate = useNavigate();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setUploadResults(null);
  };

  const handleBulkUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append('files', file);
      });

      const response = await axios.post(`${API}/calls/bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      setUploadResults(response.data);
      toast.success(`Successfully uploaded ${response.data.success_count} files!`);
      
      if (response.data.failed_count > 0) {
        toast.error(`${response.data.failed_count} files failed to upload`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFiles([]);
    setUploadResults(null);
    setUploadProgress(0);
  };

  return (
    <Layout>
      <div className="space-y-8 fade-in" data-testid="bulk-upload-page">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Bulk Call Upload
          </h1>
          <p className="text-slate-600">Upload multiple call recordings at once</p>
        </div>

        {/* Upload Section */}
        <Card className="p-8 border border-slate-200 shadow-sm">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mb-4">
              <Upload size={32} className="text-indigo-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Select Audio Files
            </h2>
            <p className="text-slate-600 mb-6">Choose multiple MP3 or WAV files to upload</p>

            <input
              type="file"
              multiple
              accept=".mp3,.wav"
              onChange={handleFileSelect}
              className="hidden"
              id="bulk-file-input"
              data-testid="bulk-file-input"
              disabled={uploading}
            />
            <label htmlFor="bulk-file-input">
              <Button
                type="button"
                variant="outline"
                className="border-2 border-dashed border-slate-300 h-auto py-4 px-8"
                disabled={uploading}
                onClick={() => document.getElementById('bulk-file-input').click()}
                data-testid="select-files-button"
              >
                <Upload size={20} className="mr-2" />
                Select Files
              </Button>
            </label>

            {selectedFiles.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-medium text-slate-700">
                    {selectedFiles.length} file(s) selected
                  </p>
                  <Button variant="ghost" size="sm" onClick={handleReset} disabled={uploading}>
                    Clear
                  </Button>
                </div>

                <div className="max-h-64 overflow-y-auto space-y-2 mb-6">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg"
                      data-testid={`file-item-${index}`}
                    >
                      <div className="flex-1 text-left">
                        <p className="text-sm font-medium text-slate-900">{file.name}</p>
                        <p className="text-xs text-slate-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {uploading && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-slate-600">Uploading...</p>
                      <p className="text-sm font-medium text-slate-900">{uploadProgress}%</p>
                    </div>
                    <Progress value={uploadProgress} className="h-2" />
                  </div>
                )}

                <Button
                  onClick={handleBulkUpload}
                  className="w-full bg-[#4338CA] hover:bg-[#3730A3] text-white"
                  disabled={uploading}
                  data-testid="upload-button"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={16} />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2" size={16} />
                      Upload All Files
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </Card>

        {/* Results Section */}
        {uploadResults && (
          <Card className="p-6 border border-slate-200 shadow-sm">
            <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Upload Results
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <Card className="p-4 border border-emerald-200 bg-emerald-50">
                <div className="flex items-center gap-3">
                  <CheckCircle2 size={32} className="text-emerald-600" />
                  <div>
                    <p className="text-sm text-slate-600">Successful Uploads</p>
                    <p className="text-3xl font-bold text-slate-900">{uploadResults.success_count}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 border border-rose-200 bg-rose-50">
                <div className="flex items-center gap-3">
                  <AlertCircle size={32} className="text-rose-600" />
                  <div>
                    <p className="text-sm text-slate-600">Failed Uploads</p>
                    <p className="text-3xl font-bold text-slate-900">{uploadResults.failed_count}</p>
                  </div>
                </div>
              </Card>
            </div>

            {uploadResults.failed_uploads && uploadResults.failed_uploads.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">Failed Files:</h3>
                <div className="space-y-2">
                  {uploadResults.failed_uploads.map((failed, index) => (
                    <div
                      key={index}
                      className="p-3 border border-rose-200 bg-rose-50 rounded-lg"
                      data-testid={`failed-file-${index}`}
                    >
                      <p className="font-medium text-slate-900">{failed.filename}</p>
                      <p className="text-sm text-rose-600">{failed.error}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <Button onClick={handleReset} variant="outline" data-testid="upload-more-button">
                Upload More Files
              </Button>
              <Button onClick={() => navigate('/dashboard')} data-testid="view-calls-button">
                View Uploaded Calls
              </Button>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default BulkUpload;